import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Droplets } from "lucide-react";
import { trackCTA } from "@/lib/analytics";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "For Consultants", to: "/for-consultants" },
  { label: "Resources", to: "/resources" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-slate-200" : "bg-white border-transparent"
      }`}
      data-testid="main-header"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex h-[72px] items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-navy-900 text-amber-500">
              <Droplets className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="leading-none">
              <span className="block font-heading text-lg font-extrabold tracking-tight text-navy-900">Wasserlab</span>
              <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-600">EHS Remote</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-navy-900 transition-colors"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center">
            <Link
              to="/free-tier-ii-assessment"
              onClick={() => trackCTA("Free Assessment", "navbar")}
              className="rounded-md bg-amber-500 px-5 py-2.5 text-sm font-bold text-navy-900 shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-amber-400"
              data-testid="nav-cta-assessment"
            >
              Get Free Assessment
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-navy-900"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-5 py-4 space-y-1">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="block rounded-md px-3 py-2.5 text-base font-semibold text-slate-800 hover:bg-slate-50"
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/free-tier-ii-assessment"
              onClick={() => trackCTA("Free Assessment", "navbar-mobile")}
              className="mt-2 block rounded-md bg-amber-500 px-3 py-3 text-center text-base font-bold text-navy-900"
              data-testid="mobile-nav-cta"
            >
              Get Free Assessment
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
